/*
 * CHESTS, STOOD UP. Asked for on 2026-09-28: "render chests oblique".
 *
 * A chest is an item on the ground (ItemChest on the server, static and
 * blocking): the ruin's, the treasure room's and the three that hold the
 * shards of Narsil, told apart by where they stand. The depot's boxes
 * (ItemDepotBox) were the same chest until 2026-10-10 (below). Since 2026-10-01 ("use a wooden chest
 * with a rounded top instead of the tall iron chest we have. make sure the
 * depot uses the same ones") every one of them is one design: a low wooden
 * chest with a rounded, barrel-vaulted lid, in warm wood with darker plank
 * seams, two iron bands up its front and over its lid, an iron rim along
 * the top of its body and a small brass lock plate. Flat, it is the picture
 * drawChest in sprites.ts draws for both items, in the same CHEST_WOOD.
 *
 * In the oblique view it stands up on its tile like the pit prop does
 * (see propFacets in oblique.ts): a box of wood CHEST_X wide and from
 * CHEST_BACK to CHEST_FRONT deep, its body CHEST_BODY_H storeys tall, and
 * on it a lid vaulted from the front edge to the back edge, CHEST_LID_H to
 * its crown: chestLidProfile, a half ellipse in LID_STAVES straight staves.
 * What shows is what the camera in the south-east sees: the front, the
 * east end in shadow with the arch of the lid on it, and the staves of the
 * lid that face the camera, lit more the higher they lie; and on them the
 * bands, the rim, the lock plate and the hasp. Whoever stands south, east
 * or west of it goes on after it, as they do round a pit prop; whether it
 * reaches over the tile north of it on the glass, putting whoever stands
 * there behind it, is read off its outline (chestCoversNorth).
 *
 * The depot's box is its own shape again since 2026-10-10 ("make depot
 * boxes a distinct square-edged chest shape"), so a depot reads apart from
 * a quest chest at a glance: the same wood, but a square-edged strongbox
 * with a flat lid, a seam where the lid sits on the body, and iron at its
 * corners in place of the bands over a vault. Flat, drawDepotBox draws it;
 * stood up, every function here takes the kind (chestKindOf) and gives the
 * depot box's facets, trim, heights and picture for "depot".
 *
 * It is kept in a file of its own so the geometry can be run by the
 * tests without a canvas, and so it touches no more of oblique.ts than
 * the constants it builds on.
 */
import { PIECE_PAD, Pt, STOREY } from "./oblique";
import { CHEST_WOOD, TILE } from "./sprites";
import { keptContext } from "./paper";

/** The quest chest's item id: ItemChest on the server. */
export const CHEST_ITEM = 12;
/**
 * The depot's box: ItemDepotBox on the server. It stands up as a chest
 * does (2026-10-01: "render the depot chests oblique"), and since
 * 2026-10-10 ("make depot boxes a distinct square-edged chest shape") it
 * is a square-edged box with a flat lid rather than the rounded chest.
 */
export const DEPOT_BOX_ITEM = 27;

/** Which item a standing chest is: a quest chest (rounded) or a depot box (square-edged). */
export type ChestKind = "chest" | "depot";

/** Whether an item stands up on its tile as a chest in the oblique view. */
export function isStandingChest(id: number): boolean {
  return id === CHEST_ITEM || id === DEPOT_BOX_ITEM;
}
export function chestKindOf(id: number): ChestKind {
  return id === DEPOT_BOX_ITEM ? "depot" : "chest";
}

/** Its west and east ends, in from its tile's west edge: where the flat picture has them. */
export const CHEST_X: Pt = [5, 27];
/** How far its back is in from its tile's north edge, in world pixels. */
export const CHEST_BACK = 6;
/** How far its front is in from its tile's north edge. */
export const CHEST_FRONT = 21;
/** How tall its body is, in storeys: the lid sits on it. Low, as the depot boxes were. */
export const CHEST_BODY_H = 0.3;
/** How tall it is to the crown of its lid, in storeys. */
export const CHEST_LID_H = 0.5;
/** How many straight staves the lid's vault is made of, front to back. */
export const LID_STAVES = 6;
/** How far the depot box's back is in from its tile's north edge: a little shallower than the chest, so its flat lid stays on its own tile. */
export const DEPOT_BACK = 7;
/** How tall the depot box's body is, in storeys, to the seam where its lid sits. */
export const DEPOT_BODY_H = 0.28;
/** How tall the depot box is to the flat top of its lid, in storeys. */
export const DEPOT_LID_H = 0.42;
/** Its body and the top of its lid, in storeys: the crown of the chest's vault, or the depot box's flat top. */
export function chestHeights(kind?: ChestKind) {
  return kind === "depot" ? { body: DEPOT_BODY_H, lid: DEPOT_LID_H } : { body: CHEST_BODY_H, lid: CHEST_LID_H };
}
/** The two iron bands, west and east edges, across it: where the flat picture has them. */
export const CHEST_BANDS: readonly Pt[] = [
  [8, 11],
  [21, 24],
];
/** The lock plate, west and east edges: in the middle of the front, as in the flat picture. */
export const CHEST_LOCK: Pt = [14, 18];

/** One facet of a standing chest, which way it faces, and for a stave of the lid how lit it is (0 to 1). */
export interface ChestFacet {
  pts: Pt[];
  side: "s" | "e" | "lid" | "top";
  lit?: number;
}

/** A point `h` storeys over (px, py) in its tile's own pixels, where the glass shows it. */
const at = (px: number, py: number, h: number): Pt => [px - h * STOREY, py - h * STOREY];

/**
 * The lid's profile, front to back: [y in from the north edge, height in
 * storeys] at the edges of its staves. A half ellipse standing on the top
 * of the body, from its front edge up to the crown over the middle and
 * down to its back edge.
 */
export function chestLidProfile(): [number, number][] {
  const mid = (CHEST_FRONT + CHEST_BACK) / 2, half = (CHEST_FRONT - CHEST_BACK) / 2;
  const out: [number, number][] = [];
  for (let i = 0; i <= LID_STAVES; i++) {
    const th = (Math.PI * i) / LID_STAVES;
    // The ends exactly, so the lid meets the body without a hair's gap.
    const y = i === 0 ? CHEST_FRONT : i === LID_STAVES ? CHEST_BACK : mid + half * Math.cos(th);
    const h = i === 0 || i === LID_STAVES ? CHEST_BODY_H : CHEST_BODY_H + (CHEST_LID_H - CHEST_BODY_H) * Math.sin(th);
    out.push([y, h]);
  }
  return out;
}

/**
 * The staves of the lid the camera sees, as indexes into the profile: a
 * stave is seen when, going back over it, it rises on the glass, which is
 * every one but those at the back that fall away more steeply than the
 * glass leans.
 */
function seenStaves(): number[] {
  const pr = chestLidProfile();
  const out: number[] = [];
  for (let i = 0; i < LID_STAVES; i++) {
    const [y0, h0] = pr[i], [y1, h1] = pr[i + 1];
    if (y1 - h1 * STOREY < y0 - h0 * STOREY) out.push(i);
  }
  return out;
}

/** A band across the lid from x0 to x1 over stave i of the profile. */
function stave(x0: number, x1: number, i: number): Pt[] {
  const pr = chestLidProfile();
  const [y0, h0] = pr[i], [y1, h1] = pr[i + 1];
  return [at(x0, y0, h0), at(x1, y0, h0), at(x1, y1, h1), at(x0, y1, h1)];
}

/**
 * The standing chest's facets in its tile's own pixels: the front of the
 * body, the east end (the body's end and the arch of the lid in one), and
 * the staves of the lid the camera sees, front to back, each with how lit
 * it is: the higher it lies the more the lamp catches it.
 */
export function chestFacets(kind?: ChestKind): ChestFacet[] {
  if (kind === "depot") return depotFacets();
  const [a, b] = CHEST_X;
  const n = CHEST_BACK, s = CHEST_FRONT, h1 = CHEST_BODY_H;
  const pr = chestLidProfile();
  const arch = pr.slice(1, -1).reverse().map(([y, h]) => at(b, y, h));
  const out: ChestFacet[] = [
    { side: "s", pts: [at(a, s, 0), at(b, s, 0), at(b, s, h1), at(a, s, h1)] },
    { side: "e", pts: [at(b, s, 0), at(b, n, 0), at(b, n, h1), ...arch, at(b, s, h1)] },
  ];
  for (const i of seenStaves()) {
    const lit = Math.sin((Math.PI * (i + 0.5)) / LID_STAVES);
    out.push({ side: "lid", pts: stave(a, b, i), lit });
  }
  return out;
}

/**
 * The depot box's facets: square-edged, so three flat faces where the
 * camera sees them: the front and the east end, each the whole height of
 * body and lid, and the flat top of the lid.
 */
function depotFacets(): ChestFacet[] {
  const [a, b] = CHEST_X;
  const n = DEPOT_BACK, s = CHEST_FRONT, h = DEPOT_LID_H;
  return [
    { side: "s", pts: [at(a, s, 0), at(b, s, 0), at(b, s, h), at(a, s, h)] },
    { side: "e", pts: [at(b, s, 0), at(b, n, 0), at(b, n, h), at(b, s, h)] },
    { side: "top", pts: [at(a, s, h), at(b, s, h), at(b, n, h), at(a, n, h)] },
  ];
}

/** One piece of ironwork or brass on a standing chest, on top of its facets. */
export interface ChestTrim {
  pts: Pt[];
  part: "band" | "rim" | "lock" | "keyhole" | "hasp" | "corner";
}

/**
 * The chest's metalwork, as the flat picture has it, laid on the facets
 * it lies on: each band up the front and over every stave of the lid the
 * camera sees; the iron rim along the top of the front, under the lid;
 * the brass lock plate on the front under it, its keyhole; and the hasp,
 * a brass strap from the lid's first stave down over the rim onto the
 * top of the plate.
 */
export function chestTrim(kind?: ChestKind): ChestTrim[] {
  if (kind === "depot") return depotTrim();
  const s = CHEST_FRONT, h1 = CHEST_BODY_H;
  const front = (x0: number, x1: number, lo: number, hi: number): Pt[] => [at(x0, s, lo), at(x1, s, lo), at(x1, s, hi), at(x0, s, hi)];
  const out: ChestTrim[] = [];
  const seen = seenStaves();
  for (const [x0, x1] of CHEST_BANDS) {
    out.push({ part: "band", pts: front(x0, x1, 0, h1) });
    for (const i of seen) out.push({ part: "band", pts: stave(x0, x1, i) });
  }
  const [a, b] = CHEST_X;
  out.push({ part: "rim", pts: front(a, b, h1 - 0.06, h1) });
  const [l0, l1] = CHEST_LOCK;
  out.push({ part: "lock", pts: front(l0, l1, h1 - 0.24, h1 - 0.04) });
  out.push({ part: "keyhole", pts: front(l0 + 1, l1 - 1, h1 - 0.17, h1 - 0.11) });
  const [y1, hh] = chestLidProfile()[1];
  const mid = (y: number) => s + (y - s) / 2;
  out.push({
    part: "hasp",
    pts: [at(l0 + 1, s, h1 - 0.1), at(l1 - 1, s, h1 - 0.1), at(l1 - 1, s, h1), at(l1 - 1, mid(y1), (h1 + hh) / 2), at(l0 + 1, mid(y1), (h1 + hh) / 2), at(l0 + 1, s, h1)],
  });
  return out;
}

/**
 * The depot box's metalwork: iron caps on its square corners, up both
 * edges of the front and the front edge of the east end and round the
 * corners of the flat top; an iron rim along the seam where the lid sits
 * on the body, round the front and the east end; and the brass lock plate
 * under the seam, its keyhole, and the hasp coming down over the seam
 * from the lid.
 */
function depotTrim(): ChestTrim[] {
  const [a, b] = CHEST_X;
  const n = DEPOT_BACK, s = CHEST_FRONT, h1 = DEPOT_BODY_H, h = DEPOT_LID_H;
  const front = (x0: number, x1: number, lo: number, hi: number): Pt[] => [at(x0, s, lo), at(x1, s, lo), at(x1, s, hi), at(x0, s, hi)];
  const end = (y0: number, y1: number, lo: number, hi: number): Pt[] => [at(b, y1, lo), at(b, y0, lo), at(b, y0, hi), at(b, y1, hi)];
  const top = (x0: number, x1: number, y0: number, y1: number): Pt[] => [at(x0, y1, h), at(x1, y1, h), at(x1, y0, h), at(x0, y0, h)];
  const C = 2.5;
  const out: ChestTrim[] = [
    { part: "rim", pts: front(a, b, h1 - 0.03, h1 + 0.03) },
    { part: "rim", pts: end(n, s, h1 - 0.03, h1 + 0.03) },
    { part: "corner", pts: front(a, a + C, 0, h) },
    { part: "corner", pts: front(b - C, b, 0, h) },
    { part: "corner", pts: end(s - C, s, 0, h) },
    { part: "corner", pts: end(n, n + C, 0, h) },
  ];
  for (const [x0, x1] of [[a, a + C], [b - C, b]]) {
    for (const [y0, y1] of [[n, n + C], [s - C, s]]) out.push({ part: "corner", pts: top(x0, x1, y0, y1) });
  }
  const [l0, l1] = CHEST_LOCK;
  out.push({ part: "lock", pts: front(l0, l1, h1 - 0.2, h1 - 0.04) });
  out.push({ part: "keyhole", pts: front(l0 + 1, l1 - 1, h1 - 0.14, h1 - 0.09) });
  out.push({ part: "hasp", pts: front(l0 + 1, l1 - 1, h1 - 0.08, h - 0.03) });
  return out;
}

/**
 * Whether a standing chest reaches over the tile north of it on the glass,
 * where it covers a figure standing there, who is behind it: read off the
 * outline it is drawn by, as coversNorth reads a pit prop's.
 */
export function chestCoversNorth(kind?: ChestKind): boolean {
  return chestFacets(kind).some((f) => f.pts.some(([, py]) => py < 0));
}

/**
 * The chest's colours, the flat picture's CHEST_WOOD: the front, the east
 * end in shadow, the lid from its lowest stave to its crown, the plank
 * seams, the iron, and the brass of the lock.
 */
export function chestFills(_kind?: ChestKind) {
  return CHEST_WOOD;
}

/** A colour between two #rrggbb colours, t of the way from the first. */
function mix(c0: string, c1: string, t: number): string {
  const ch = (c: string, i: number) => parseInt(c.slice(1 + 2 * i, 3 + 2 * i), 16);
  const v = [0, 1, 2].map((i) => Math.round(ch(c0, i) + (ch(c1, i) - ch(c0, i)) * t));
  return `rgb(${v[0]},${v[1]},${v[2]})`;
}

const TRIM_FILL = { band: CHEST_WOOD.band, rim: CHEST_WOOD.band, corner: CHEST_WOOD.band, lock: CHEST_WOOD.lock, keyhole: CHEST_WOOD.keyhole, hasp: CHEST_WOOD.hasp };
let chestCache: HTMLCanvasElement | undefined;
let depotCache: HTMLCanvasElement | undefined;
/**
 * The standing chest's picture, made once and kept, whichever item it is:
 * PIECE_PAD bigger than the tile on every side, like the pit prop's, since
 * it leans up and to the left out of its square.
 */
export function chestPicture(kind?: ChestKind): HTMLCanvasElement {
  if (kind === "depot") return (depotCache ??= depotPicture());
  if (chestCache) return chestCache;
  const W = CHEST_WOOD;
  const c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const path = (pts: readonly Pt[]) => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
  };
  const line = (p: Pt, q: Pt) => {
    ctx.beginPath();
    ctx.moveTo(p[0], p[1]);
    ctx.lineTo(q[0], q[1]);
    ctx.stroke();
  };
  for (const f of chestFacets()) {
    path(f.pts);
    ctx.fillStyle = f.side === "s" ? W.s : f.side === "e" ? W.e : mix(W.lidLow, W.lidHigh, f.lit ?? 0);
    ctx.fill();
  }
  // The planks: a seam half way up the body, round its front and its east
  // end, and one between every two staves of the lid.
  const [a, b] = CHEST_X;
  const n = CHEST_BACK, s = CHEST_FRONT, h1 = CHEST_BODY_H;
  ctx.strokeStyle = W.plank;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.6;
  line(at(a, s, h1 / 2), at(b, s, h1 / 2));
  line(at(b, s, h1 / 2), at(b, n, h1 / 2));
  const pr = chestLidProfile();
  for (const i of seenStaves()) if (i > 0) line(at(a, pr[i][0], pr[i][1]), at(b, pr[i][0], pr[i][1]));
  ctx.globalAlpha = 1;
  // The corner where the front meets the east end, a shade darker.
  ctx.strokeStyle = "rgba(30,14,4,0.35)";
  line(at(b, s, 0), at(b, s, h1));
  for (const tr of chestTrim()) {
    path(tr.pts);
    ctx.fillStyle = TRIM_FILL[tr.part];
    ctx.fill();
  }
  // The arch of the lid on the east end, an iron band round its edge.
  ctx.strokeStyle = W.band;
  ctx.beginPath();
  pr.forEach(([y, h], i) => {
    const [x, yy] = at(b - 0.5, y, h);
    if (i) ctx.lineTo(x, yy);
    else ctx.moveTo(x, yy);
  });
  ctx.stroke();
  chestCache = c;
  return c;
}

/**
 * The depot box's picture, PIECE_PAD bigger than the tile as the chest's
 * is: its front and east end in the chest's wood, its flat top lit as the
 * crown of the chest's lid is; a plank seam half way up the body, the dark
 * seam where the lid sits on it, and two planks across the top; then its
 * iron and brass.
 */
function depotPicture(): HTMLCanvasElement {
  const W = CHEST_WOOD;
  const c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const path = (pts: readonly Pt[]) => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
  };
  const line = (p: Pt, q: Pt) => {
    ctx.beginPath();
    ctx.moveTo(p[0], p[1]);
    ctx.lineTo(q[0], q[1]);
    ctx.stroke();
  };
  for (const f of depotFacets()) {
    path(f.pts);
    ctx.fillStyle = f.side === "s" ? W.s : f.side === "e" ? W.e : W.lidHigh;
    ctx.fill();
  }
  const [a, b] = CHEST_X;
  const n = DEPOT_BACK, s = CHEST_FRONT, h1 = DEPOT_BODY_H, h = DEPOT_LID_H;
  ctx.strokeStyle = W.plank;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.6;
  line(at(a, s, h1 / 2), at(b, s, h1 / 2));
  line(at(b, s, h1 / 2), at(b, n, h1 / 2));
  for (const y of [n + (s - n) / 3, n + (2 * (s - n)) / 3]) line(at(a, y, h), at(b, y, h));
  ctx.globalAlpha = 1;
  // The lid's seam, darker than a plank's, round the front and the east end.
  line(at(a, s, h1 + 0.04), at(b, s, h1 + 0.04));
  line(at(b, s, h1 + 0.04), at(b, n, h1 + 0.04));
  // The square edges, a shade darker: front to east end, front and east
  // end to top, and round the top's back and west edges, so it reads as a
  // box and not a stack of boards.
  ctx.strokeStyle = "rgba(30,14,4,0.55)";
  line(at(b, s, 0), at(b, s, h));
  path(depotFacets()[2].pts);
  ctx.stroke();
  for (const tr of depotTrim()) {
    path(tr.pts);
    ctx.fillStyle = TRIM_FILL[tr.part];
    ctx.fill();
  }
  return c;
}
